import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { toast } from "sonner";

import { translate } from "@/constants/i18n";
import { queryClient } from "@/services/query-client";
import type { ProblemDetail, TokenPair } from "@/meta/types";
import { authSnapshot, useAuthStore } from "@/store/auth";
import { useLocaleStore } from "@/store/locale";

export const api = axios.create({
  // Lists go over as repeated keys (a=1&a=2), which is what FastAPI reads.
  paramsSerializer: { indexes: null },
  baseURL: `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v1`,
  headers: { "Content-Type": "application/json" },
  // A minute before giving up. A search over tens of millions of members can
  // take a while and is worth waiting for; abandoning it at twenty seconds only
  // means asking again. The server is told when we do give up — see the
  // cancellation watcher on the session — so a wait nobody is having is not a
  // query nobody is having.
  timeout: 60_000,
});

/**
 * One request to a server that is not answering, not one per query on screen.
 *
 * A page holds several reads and fires them together — the shell asks for the
 * profile and the policy, a list asks for its rows and its lookups — so an API
 * that is down was met by the whole set at once, each waiting out its own
 * connection timeout, before anything could tell the person what had happened.
 * They cannot check on each other: they start in the same tick.
 *
 * So the first failure closes the door behind it. While it is shut, every
 * request is refused here without a socket being opened, with the same error
 * the real attempt would have produced — so callers cannot tell the difference
 * and none of them has to know this exists.
 *
 * It re-opens on its own after `DOWN_WINDOW`, and the first request through a
 * re-opened door is the only one that reaches the network — if it fails too, it
 * shuts the door again behind itself. The window is short because it is a burst
 * this is for, and a burst is simultaneous: five seconds is far longer than the
 * milliseconds between one page's reads, and far shorter than the time anyone
 * takes to press Try again, so a person retrying is never turned away by it.
 */
const DOWN_WINDOW = 5_000;

/** When the door shut. `0` is open. */
let downSince = 0;

const doorIsShut = () => downSince !== 0 && Date.now() - downSince < DOWN_WINDOW;

api.interceptors.request.use((config) => {
  // Refused before a socket is opened, and shaped exactly like the failure it
  // stands in for — no response, so `isUnreachable` and `errorMessage` read it
  // the same way they read the real thing.
  if (doorIsShut()) {
    throw new AxiosError("Cannot reach the server.", AxiosError.ERR_NETWORK, config);
  }

  const { accessToken } = authSnapshot();
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  // A file goes as multipart, and only the browser can write the boundary. The
  // instance default above would otherwise stay on the request and the body
  // would be run through the JSON transform, which turns a FormData into `{}`
  // — the file silently gone, with a 200-shaped request to show for it.
  if (config.data instanceof FormData) config.headers.delete("Content-Type");
  // The backend writes its error messages in this language; "en" is its default.
  config.headers["Accept-Language"] = useLocaleStore.getState().locale;
  return config;
});

/**
 * A single refresh in flight, shared by every request that hits a 401 at once.
 * Without this, a page that fires five queries on mount would rotate the
 * refresh token five times — and the backend treats a replayed refresh token as
 * theft, which would sign the user out of everything.
 */
let refreshInFlight: Promise<TokenPair> | null = null;

/**
 * Shared, so nothing rotates the refresh token twice.
 *
 * The single flight above is the whole point: the backend treats a replayed
 * refresh token as theft and revokes every session for that account, so two
 * callers refreshing at once would sign the person out of everything. The SSE
 * client cannot go through the interceptors — it is `fetch`, for a body read
 * as it arrives — so it comes here instead of refreshing on its own.
 */
export async function refreshSession(): Promise<TokenPair> {
  const tokens = await refreshTokens();
  useAuthStore.getState().setTokens(tokens.access_token, tokens.refresh_token);
  return tokens;
}

async function refreshTokens(): Promise<TokenPair> {
  const { refreshToken } = authSnapshot();
  if (!refreshToken) throw new Error("No refresh token");

  refreshInFlight ??= axios
    .post<TokenPair>(`${api.defaults.baseURL}/auth/refresh`, {
      refresh_token: refreshToken,
    })
    .then((response) => response.data)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/**
 * The header an update or delete carries: the record as it was last read.
 *
 * The server compares it with the row as it stands and refuses a save over
 * someone else's with 412 — see `stale` below — so two people editing one
 * record cannot silently overwrite each other. Pass the record the screen
 * holds; a record without a timestamp sends nothing, and the server answers
 * 428 so the gap is noticed rather than waved through.
 */
export function ifMatch(record: { updated_at?: string | null | undefined } | null | undefined) {
  return record?.updated_at ? { headers: { "If-Match": record.updated_at } } : {};
}

/** 412: the record moved under the person. 409 `record_busy`: somebody is saving it right now. */
function isStale(error: AxiosError<ProblemDetail>) {
  return error.response?.status === 412;
}
function isBusy(error: AxiosError<ProblemDetail>) {
  return error.response?.status === 409 && error.response.data?.type?.endsWith("#record_busy");
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

api.interceptors.response.use(
  (response) => {
    // Anything at all came back, so the server is there.
    downSince = 0;
    return response;
  },
  async (error: AxiosError<ProblemDetail>) => {
    const config = error.config as RetriableConfig | undefined;
    const isAuthCall = config?.url?.startsWith("/auth/");

    // An error the server *sent* means it is up and had something to say, even
    // if that thing was a 500. Only silence shuts the door — and a request this
    // interceptor refused is already behind it, so it does not reset the clock.
    if (isUnreachable(error)) {
      if (!doorIsShut()) downSince = Date.now();
    } else if (error.response) {
      downSince = 0;
    }

    // An administrator forced a password change mid-session: the API now walls
    // off everything but the change, so go there. The store is persisted, so a
    // full navigation keeps the session.
    if (
      error.response?.status === 403 &&
      error.response.data?.type?.endsWith("#password_change_required") &&
      !window.location.pathname.startsWith("/change-password")
    ) {
      window.location.assign("/change-password");
    }

    // Somebody else got there first. Everything held is refetched, so the
    // screen shows what is actually there and the person edits from that; the
    // mutation still rejects, so the form keeps their draft on screen to redo.
    if (isStale(error)) {
      toast.error(translate(useLocaleStore.getState().locale, "common.staleRecord"));
      void queryClient.invalidateQueries();
    } else if (isBusy(error)) {
      toast.error(translate(useLocaleStore.getState().locale, "common.recordBusy"));
    }

    if (error.response?.status === 401 && config && !config._retried && !isAuthCall) {
      config._retried = true;
      try {
        const tokens = await refreshTokens();
        useAuthStore.getState().setTokens(tokens.access_token, tokens.refresh_token);
        config.headers.Authorization = `Bearer ${tokens.access_token}`;
        return api.request(config);
      } catch {
        useAuthStore.getState().signOut();
      }
    }

    return Promise.reject(error);
  },
);

/** Pull a human-readable message out of a problem+json response. */
/**
 * No answer at all — the server is down, restarting, or the network is gone.
 *
 * Different in kind from an error the server *sent*: that one has something to
 * say for itself and is usually about the request, while this one is about
 * everything and clears on its own. The two deserve different screens, so the
 * test lives here rather than being spelled out at each of them.
 */
export function isUnreachable(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response;
}

export function errorMessage(error: unknown, fallback = "Something went wrong."): string {
  if (axios.isAxiosError<ProblemDetail>(error)) {
    const problem = error.response?.data;
    if (problem?.errors?.length) {
      return problem.errors.map((item) => item.msg).join(". ");
    }
    if (problem?.detail) return problem.detail;
    if (isUnreachable(error)) return "Cannot reach the server.";
  }
  return fallback;
}
